import {
  isFederatedSessionSignedOut,
  readFederatedSession,
  readImpersonationSession,
  useConfigControllerGetPublicTenantConfig,
} from 'korint-sdk';
import {
  useAddBearerTokenInAxios,
  useAnonymousSession,
  useAuth,
  useKorintConfig,
} from 'korint-sdk/react';
import { CustomFunnelAuth } from 'korint-sdk/react';
import { Loader2Icon } from 'lucide-react';
import type { ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { validateRedirectUrl } from '../../../utils/validate-redirect';
import { AnonymousSessionProvider } from './anonymous-session-context';
import { isMfaSetupSkippedForSession } from './mfaSetupSkip';
import { useMfaEnrollment } from './useMfaEnrollment';
import { Center } from '@folio/components/ui/flex';

export function AuthRequired({ children, auth }: { children: ReactNode; auth?: CustomFunnelAuth }) {
  const authState = useAuth();
  const { clientOptions } = useKorintConfig();
  const { pathname, search } = useLocation();
  const { data: tenantConfig } = useConfigControllerGetPublicTenantConfig();
  const mfa = tenantConfig?.authentication?.mfa;
  const enrollment = useMfaEnrollment({
    enabled: authState.authStatus === 'authenticated' && Boolean(mfa?.authenticator || mfa?.sms),
  });

  const isAnonymousCandidate =
    auth === CustomFunnelAuth.ANONYMOUS && authState.authStatus === 'unauthenticated';

  const {
    session,
    isLoading: isAnonymousLoading,
    isError: isAnonymousError,
    clearSession,
  } = useAnonymousSession({
    enabled: isAnonymousCandidate,
  });

  const token = useAddBearerTokenInAxios(isAnonymousCandidate ? session?.accessToken : undefined);

  // A Korint-staff impersonation session has no Amplify session at all, so
  // `authStatus` is 'unauthenticated' — checked before the branches below or the
  // guard would bounce a perfectly valid session to sign-in. Also skips the MFA
  // prompt: there is no Cognito session to enrol.
  //
  // Still gated on `token`, like the authenticated branch below: it is what tells
  // us `useAddBearerTokenInAxios` has registered its request interceptor. Render
  // children before that and their first fetch goes out with no Authorization
  // header, which the gateway answers with a 401 nothing retries.
  if (readImpersonationSession()) {
    if (!token) {
      return (
        <Center className="fixed inset-0">
          <Loader2Icon className="w-8 h-8 animate-spin" />
        </Center>
      );
    }
    return children;
  }

  // Brokers of a federated app have no Amplify session: they come back through their provider.
  if (clientOptions.federatedLogin) {
    if (readFederatedSession()) {
      if (!token) {
        return (
          <Center className="fixed inset-0">
            <Loader2Icon className="w-8 h-8 animate-spin" />
          </Center>
        );
      }
      return children;
    }
    const entry = isFederatedSessionSignedOut()
      ? '/auth/refused?reason=signed_out'
      : `/auth/federated?target=${encodeURIComponent(validateRedirectUrl(pathname + search))}`;
    return <Navigate to={entry} replace />;
  }

  // Auth still configuring → show loader
  if (authState.authStatus === 'configuring')
    return (
      <Center className="fixed inset-0">
        <Loader2Icon className="w-8 h-8 animate-spin" />
      </Center>
    );

  // Authenticated user on anonymous funnel → redirect to auth/me
  if (authState.authStatus === 'authenticated' && auth === CustomFunnelAuth.ANONYMOUS)
    return <Navigate to="/auth/me" replace />;

  // Authenticated via Cognito → render children
  if (authState.authStatus === 'authenticated' && token) {
    if (mfa?.authenticator || mfa?.sms) {
      if (enrollment.isLoading)
        return (
          <Center className="fixed inset-0">
            <Loader2Icon className="w-8 h-8 animate-spin" />
          </Center>
        );
      if (enrollment.shouldPromptSetup && !isMfaSetupSkippedForSession()) {
        const validatedRedirect = validateRedirectUrl(pathname + search);
        return (
          <Navigate
            to={{
              pathname: '/auth/mfa-setup',
              search: `?redirect=${encodeURIComponent(validatedRedirect)}`,
            }}
            replace
          />
        );
      }
    }
    return children;
  }

  // Unauthenticated + anonymous NOT enabled → redirect to /auth
  if (authState.authStatus === 'unauthenticated' && auth !== CustomFunnelAuth.ANONYMOUS) {
    const validatedRedirect = validateRedirectUrl(pathname + search);
    return (
      <Navigate
        to={{ pathname: '/auth', search: `?redirect=${encodeURIComponent(validatedRedirect)}` }}
        replace
      />
    );
  }

  // Expired/claimed anonymous session → sign-in is the remaining path to the quote
  if (isAnonymousError) {
    const validatedRedirect = validateRedirectUrl(pathname + search);
    return (
      <Navigate
        to={{ pathname: '/auth', search: `?redirect=${encodeURIComponent(validatedRedirect)}` }}
        replace
      />
    );
  }

  // Unauthenticated + anonymous enabled, loading → show loader
  if (isAnonymousLoading || !session)
    return (
      <Center className="fixed inset-0">
        <Loader2Icon className="w-8 h-8 animate-spin" />
      </Center>
    );

  // Anonymous session ready → render children with context
  return <AnonymousSessionProvider value={{ clearSession }}>{children}</AnonymousSessionProvider>;
}
